import React, { createContext, ReactNode, useContext, useState } from 'react'
import { useTheme } from '@mui/material'

import Box from 'components_new/atoms/Box'

import BannerBar from 'components_new/organisms/BannerBar'
import GlobalMobileNavigation from 'components_new/organisms/GlobalMobileNavigation'
import Navbar from 'components_new/organisms/Navbar'

import { useNavigation } from 'redux/hooks/NavigationMenuStore'
import { useBackgroundColor } from 'redux/hooks/Customization'
import { useIsMobile } from 'utils/hooks'

interface GlobalLayoutProps {
  children: ReactNode
  navbarProps?: {
    content?: ReactNode
    footer?: ReactNode
    header?: ReactNode
    menu?: ReactNode
  }
  setBgcolor?: boolean
}

interface MobileNavContextValue {
  setShowMobileNav: (value: boolean) => void
  showMobileNav: boolean
}

const MobileNavContext = createContext<MobileNavContextValue>({
  setShowMobileNav: () => {},
  showMobileNav: false
})

export const useMobileNav = () => useContext(MobileNavContext)

export const MOBILE_FOOTER_HEIGHT = 56

export const NAVBAR_WIDTH_COLLAPSED = 53
export const NAVBAR_MIN_WIDTH_OPEN = 256
export const NAVBAR_MAX_WIDTH_OPEN = 500

const GlobalLayout = (props: GlobalLayoutProps) => {
  const { setBgcolor, children, navbarProps } = props
  const bgcolorToUse = useBackgroundColor()
  const bgcolor = setBgcolor ? bgcolorToUse : undefined

  const theme = useTheme()
  const isMobile = useIsMobile()

  const { open: navOpen } = useNavigation()

  const [showMobileNav, setShowMobileNav] = useState(false)

  // check if the user agent is actually a phone:
  const userAgent = window.navigator.userAgent
  const isPhone = /iPhone|iPad|iPod|Android/i.test(userAgent)

  if (isMobile)
    return (
      <MobileNavContext.Provider value={{ setShowMobileNav, showMobileNav }}>
        <Box
          component="main"
          sx={{
            bgcolor: bgcolor ?? 'background.paper',
            height: `calc(100${isPhone ? 'svh' : 'vh'} - ${
              showMobileNav ? MOBILE_FOOTER_HEIGHT : 0
            }px)`,
            position: 'relative'
          }}
        >
          {children}

          {/*-- footer bleed --*/}
          {showMobileNav ? (
            <Box
              sx={{
                height: MOBILE_FOOTER_HEIGHT,
                minHeight: MOBILE_FOOTER_HEIGHT,
                bgcolor: bgcolor ?? 'background.paper'
              }}
            />
          ) : null}
        </Box>

        {/*-- navbar --*/}
        {showMobileNav ? (
          <Box
            component="footer"
            sx={{
              bgcolor: 'background.paper',
              scrollbarGutter: 'stable',
              overflow: 'hidden',
              position: 'fixed',
              width: '100%',
              bottom: 0,
              left: 0,
              zIndex: theme.zIndex.appBar,
              height: MOBILE_FOOTER_HEIGHT,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <GlobalMobileNavigation
              sx={{
                bgcolor: 'background.default',
                flex: 0,
                minHeight: MOBILE_FOOTER_HEIGHT
              }}
            />
          </Box>
        ) : null}
      </MobileNavContext.Provider>
    )

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          height: '100vh',
          minHeight: 0
        }}
      >
        {/*-- banner --*/}
        <BannerBar />
        <Box
          sx={{
            position: 'relative',
            height: '100%',
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'row',
            overflowX: 'hidden',
            bgcolor: bgcolor ?? 'background.default',
            minHeight: 0
          }}
        >
          {/*-- navbar --*/}
          <Box
            sx={{
              position: 'relative',
              height: '100%',
              // eslint-disable-next-line max-len
              minWidth: navOpen
                ? NAVBAR_MIN_WIDTH_OPEN
                : NAVBAR_WIDTH_COLLAPSED,
              // eslint-disable-next-line max-len
              maxWidth: navOpen
                ? NAVBAR_MAX_WIDTH_OPEN
                : NAVBAR_WIDTH_COLLAPSED,
              flexBasis: navOpen ? '8%' : undefined,
              transition: 'all 0.1s',
              // eslint-disable-next-line max-len
              transitionTimingFunction: theme.transitions.easing.easeInOut,
              bgcolor: bgcolor ?? 'background.default',
              borderRight: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: '100%',
                overflow: 'auto'
              }}
            >
              <Navbar footer={navbarProps?.footer} />
            </Box>
          </Box>

          <Box
            sx={{
              position: 'relative',
              height: '100%',
              flex: '1 1 auto',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </>
  )
}

GlobalLayout.displayName = 'GlobalLayout'
export default GlobalLayout
